import { NetworkStatusOptions, USStateOptions } from '@strata/constants';
import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { FormContainer } from '@strata/react-form-container';
import { InlineMessage } from '@strata/react-inline-message';
import { PhoneInputFormik, TextInputFormik } from '@strata/react-inputs';
import { DetailLabel } from '@strata/react-label-value-pairs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';

import { AdditionalOpsContactsFields } from './AdditionalOpsContactsFields';
import { OrtfConfigurationFields } from './OrtfConfigurationFields';
import { PayToInformationFields } from './PayToInformationFields';

import {
    ACCREDITATION_OPTIONS,
    ORG_SIZE_OPTIONS,
    PHARMACY_TYPE_OPTIONS,
    PSAO_OPTIONS,
    SWITCH_PROVIDER_OPTIONS,
} from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { put, useGetWithRefetch } from 'services/Http';
import {
    loadOrtfValues,
    loadSupplierValues,
    packageOrtfConfig,
    packageSupplier,
    supplierSchema,
} from 'services/NPISupport';

const YES_NO_OPTIONS = ['Yes', 'No'];

export const GeneralInfoTab = ({ onSecondaryButtonClick = undefined }) => {
    const { npi } = useParams();
    const { result: supplier, refetch: refetchSupplier } = useGetWithRefetch(
        `/reference/suppliers/${npi}`,
    );
    const { result: ortfConfig, refetch: refetchOrtfConfig } = useGetWithRefetch(
        `/reference/suppliers/ortf_config/${npi}`,
    );

    return (
        <Formik
            initialValues={{
                ...loadSupplierValues(supplier),
                ...loadOrtfValues(ortfConfig),
            }}
            validationSchema={supplierSchema}
            enableReinitialize
            onSubmit={async (values, { resetForm }) => {
                try {
                    const body = packageSupplier(values);
                    if (body.primary_contact_email) {
                        await put(`/worklist/admin/tasks/update_email/${values.npi}`, {
                            email: body.primary_contact_email,
                        });
                    }
                    await put(`/reference/suppliers/${values.npi}`, body);
                    await put(
                        `/reference/suppliers/ortf_config/${values.npi}`,
                        packageOrtfConfig(values, ortfConfig),
                    );
                    SuccessToast({ message: 'General info updated successfully.' });
                    await refetchSupplier();
                    await refetchOrtfConfig();
                    resetForm();
                } catch (err) {
                    handleError('Error updating general info.', err);
                }
            }}
        >
            {(formik) => (
                <FormContainer
                    formTitle="Edit Supplier"
                    isLoading={!supplier || ortfConfig === undefined}
                    onFormSubmit={formik.handleSubmit}
                    isSubmitting={formik.isSubmitting || !formik.isValid || !formik.dirty}
                    onSecondaryButtonClick={onSecondaryButtonClick ?? formik.resetForm}
                    formLeftContent={
                        <>
                            <div className="margin__bottom--medium font-bold--uppercase">
                                Organization Information
                            </div>
                            <TextInputFormik label="Intake Supplier NPI" id="npi" disabled />
                            <TextInputFormik
                                label="Billing Supplier NPI"
                                id="billing_supplier_npi"
                                required
                            />
                            <TextInputFormik
                                label="Organization Name"
                                id="organization_name"
                                required
                            />
                            <SuggestSingleSelectFormik
                                id="org_size"
                                label="Organization Size"
                                required
                                values={ORG_SIZE_OPTIONS}
                            />
                            <SuggestSingleSelectFormik
                                id="pharmacy_type"
                                label="Pharmacy Type"
                                values={PHARMACY_TYPE_OPTIONS}
                            />
                            <SuggestSingleSelectFormik
                                id="psao"
                                label="PSAO"
                                values={PSAO_OPTIONS}
                            />
                            <PhoneInputFormik
                                allowExtension
                                label="Pharmacy Phone Number"
                                id="telephone_number"
                                required
                            />
                            <PhoneInputFormik label="Fax Number" id="fax_number" />
                            <TextInputFormik label="Address Line 1" id="address_1" required />
                            <TextInputFormik label="Address Line 2" id="address_2" />
                            <TextInputFormik label="City" id="city" required />
                            <SuggestSingleSelectFormik
                                label="State"
                                id="state"
                                required
                                values={USStateOptions}
                                selectedItem={USStateOptions.find(
                                    ({ secondaryData }) => secondaryData === formik.values.state,
                                )}
                                onChange={(newVal) =>
                                    formik.setFieldValue('state', newVal?.secondaryData)
                                }
                            />
                            <TextInputFormik label="Postal Code" id="postal_code" required />
                            <TextInputFormik label="Tax ID" id="tax_id" required />
                            <CheckboxListFormik
                                name="accreditations"
                                label="Accreditations"
                                options={ACCREDITATION_OPTIONS}
                            />

                            <div className="margin__bottom--medium font-bold--uppercase">
                                Operations Contact Information
                            </div>
                            <span className="font-regular--uppercase margin__bottom--medium">
                                Primary Contact
                            </span>
                            <TextInputFormik
                                label="Primary Ops Contact First Name"
                                id="first_name"
                                required
                            />
                            <TextInputFormik
                                label="Primary Ops Contact Last Name"
                                id="last_name"
                                required
                            />
                            <TextInputFormik
                                label="Primary Ops Contact Email"
                                id="primary_contact_email"
                            />
                            <PhoneInputFormik
                                allowExtension
                                label="Primary Ops Contact Phone"
                                id="primary_contact_phone"
                                required
                            />
                            <span className="font-regular--uppercase margin__bottom--medium">
                                Secondary Contact
                            </span>
                            <TextInputFormik
                                label="Secondary Ops Contact First Name"
                                id="secondary_contact_first_name"
                            />
                            <TextInputFormik
                                label="Secondary Ops Contact Last Name"
                                id="secondary_contact_last_name"
                            />
                            <TextInputFormik
                                label="Secondary Ops Contact Email"
                                id="secondary_contact_email"
                            />
                            <PhoneInputFormik
                                allowExtension
                                label="Secondary Ops Contact Phone"
                                id="secondary_contact_phone"
                            />
                            <AdditionalOpsContactsFields />

                            <PayToInformationFields />

                            <div className="margin__bottom--medium font-bold--uppercase">
                                Other Contact Information
                            </div>
                            <TextInputFormik
                                label="Business Contact Name"
                                id="businessops_contact_name"
                            />
                            <TextInputFormik
                                label="Business Contact Email"
                                id="businessops_contact_email"
                            />
                            <PhoneInputFormik
                                allowExtension
                                label="Business Contact Phone"
                                id="businessops_contact_phone"
                            />
                            <TextInputFormik
                                label="Contracting Contact Name"
                                id="contracting_contact_name"
                            />
                            <TextInputFormik
                                label="Contracting Contact Email"
                                id="contracting_contact_email"
                            />
                            <PhoneInputFormik
                                allowExtension
                                label="Contracting Contact Phone"
                                id="contracting_contact_phone"
                            />
                            <TextInputFormik label="Legal Notice Email" id="legal_notice_email" />
                            <TextInputFormik label="Breach Notice Email" id="breach_notice_email" />

                            <div className="margin__bottom--medium font-bold--uppercase">
                                Additional Information
                            </div>
                            <SuggestSingleSelectFormik
                                id="network_status"
                                label="Network Status"
                                required
                                values={NetworkStatusOptions}
                                onChange={(newValue) =>
                                    formik.setFieldValue('network_status', newValue.value)
                                }
                                selectedItem={NetworkStatusOptions.find(
                                    ({ value }) => value === formik.values.network_status,
                                )}
                            />
                            <TextInputFormik label="Business Hours" id="business_hours" />
                            <TextInputFormik label="Supplier Website" id="pharmacy_website" />
                            <SuggestSingleSelectFormik
                                label="Switch Provider"
                                id="switch_provider"
                                values={SWITCH_PROVIDER_OPTIONS}
                            />
                            <TextInputFormik label="Pharmacy Software" id="pharmacy_software" />
                            <DetailLabel label="Send do not Fill Notifications?" />
                            <RadioButtonGroupFormik
                                name="send_do_not_fill"
                                options={YES_NO_OPTIONS}
                                inline
                            />
                            <DetailLabel label="Supplier owns PAA?" />
                            <RadioButtonGroupFormik
                                name="does_own_paa"
                                options={YES_NO_OPTIONS}
                                inline
                            />
                            {supplier?.integration_available && (
                                <>
                                    <div className="padding__bottom--medium">
                                        <InlineMessage
                                            label={
                                                supplier?.allow_medical
                                                    ? 'This supplier is configured to receive e-referrals.'
                                                    : 'This supplier is set up to receive e-referrals for pharmacy benefit. Referrals for medical benefit will continue to be transmitted via fax.'
                                            }
                                            intent={InlineMessage.Intent.INFO}
                                        />
                                    </div>
                                    <DetailLabel label="Send assignment fax in addition to e-referral?" />
                                    <RadioButtonGroupFormik
                                        name="also_send_fax"
                                        options={YES_NO_OPTIONS}
                                        inline
                                    />
                                </>
                            )}
                            <OrtfConfigurationFields />
                        </>
                    }
                />
            )}
        </Formik>
    );
};

GeneralInfoTab.propTypes = {
    onSecondaryButtonClick: PropTypes.func,
};
